import React, { useCallback, useEffect, useRef, useState } from 'react'
import { bookInfoEndpoints } from '@URLS'
import { CoverpageContextMenu } from './CoverpageContextMenu'
import { ContributorsModal } from './ContributorsModal'
import { useInlineTextEdit } from './useInlineTextEdit'

const AUTHOR_MODE_CLASS = 'coverpage-author-mode'

// Right-click author tools for the title page. Follows the same Author Tools toggle as the
// book pages (jStorage options_advanced_edit_mode, set by author/scripts/tools.jsm and
// announced through the advanced-edit-mode-changed event). The editable regions are the
// server-rendered elements tagged with data-author-region in web.coverpage.html.
export const App = ({ coursenum, allowQuill }) => {
  const [authorMode, setAuthorMode] = useState(() => {
    try {
      return !!(
        window.$ &&
        window.$.jStorage &&
        window.$.jStorage.get('options_advanced_edit_mode')
      )
    } catch (e) {
      return false
    }
  })
  const [menu, setMenu] = useState(null) // { x, y, region, opened }
  const opens = useRef(0)
  const [contributorsOpen, setContributorsOpen] = useState(false)

  // Every save writes a whole new docbook version, so only one may be in flight
  // (same gate as the ToC editor, client/course_toc/components/App.js).
  const [isBusy, setIsBusy] = useState(false)
  const busyRef = useRef(false)
  const runMutation = useCallback(async fn => {
    if (busyRef.current) return
    busyRef.current = true
    setIsBusy(true)
    try {
      await fn()
    } finally {
      busyRef.current = false
      setIsBusy(false)
    }
  }, [])

  const { startEdit } = useInlineTextEdit({ coursenum, runMutation })

  useEffect(() => {
    const handleToggle = e => setAuthorMode(!!(e.detail && e.detail.enabled))
    window.addEventListener('advanced-edit-mode-changed', handleToggle)
    return () =>
      window.removeEventListener('advanced-edit-mode-changed', handleToggle)
  }, [])

  useEffect(() => {
    document.body.classList.toggle(AUTHOR_MODE_CLASS, authorMode)
    if (!authorMode) setMenu(null)
    return () => document.body.classList.remove(AUTHOR_MODE_CLASS)
  }, [authorMode])

  useEffect(() => {
    if (!authorMode) return undefined
    const handleContextMenu = event => {
      const target =
        event.target && event.target.closest
          ? event.target.closest('[data-author-region]')
          : null
      if (!target) return
      // the element being typed into keeps the browser's own menu (copy/paste)
      if (target.isContentEditable) return
      event.preventDefault()
      opens.current += 1
      setMenu({
        x: event.pageX,
        y: event.pageY,
        region: target.dataset.authorRegion,
        opened: opens.current,
      })
    }
    document.addEventListener('contextmenu', handleContextMenu)
    return () => document.removeEventListener('contextmenu', handleContextMenu)
  }, [authorMode])

  const closeMenu = useCallback(() => setMenu(null), [])

  const menuItems = region => {
    const editTitle = {
      key: 'title',
      label: 'Edit title',
      icon: 'fa-regular fa-pencil',
      onSelect: () => startEdit('title'),
    }
    const editSubtitle = {
      key: 'subtitle',
      label: 'Edit subtitle',
      icon: 'fa-regular fa-pencil',
      onSelect: () => startEdit('subtitle'),
    }
    switch (region) {
      case 'title':
        return [editTitle, editSubtitle]
      case 'subtitle':
        return [editSubtitle, editTitle]
      case 'contributors':
        return [
          {
            key: 'contributors',
            label: 'Edit contributors',
            icon: 'fa-regular fa-users',
            onSelect: () => setContributorsOpen(true),
          },
        ]
      case 'overview':
        return [
          {
            key: 'overview',
            label: 'Edit overview (Beta)',
            icon: 'fa-regular fa-pencil',
            disabled: !allowQuill,
            title: allowQuill
              ? undefined
              : 'The beta content editor is not enabled for you',
            onSelect: () => {
              window.location.assign(
                bookInfoEndpoints.quillOverviewPage({ coursenum })
              )
            },
          },
        ]
      default:
        return []
    }
  }

  return (
    <React.Fragment>
      {menu && (
        <CoverpageContextMenu
          x={menu.x}
          y={menu.y}
          opened={menu.opened}
          header="Title page"
          items={menuItems(menu.region)}
          onClose={closeMenu}
        />
      )}
      <ContributorsModal
        coursenum={coursenum}
        show={contributorsOpen}
        onClose={() => setContributorsOpen(false)}
        runMutation={runMutation}
        isBusy={isBusy}
      />
    </React.Fragment>
  )
}
